import { alpha } from '@mui/material/styles'

import {
  categoricalTokens,
  chartTokens,
  colorTokens,
  semanticTokens,
} from './tokens.generated'

// ----------------------------------------------------------------------
// The backoffice palette, assembled from the generated design tokens.
//
// Nothing here declares a colour. Every value comes from
// `design-system/beast.tokens.json` via `tokens.generated.ts`, which is what
// keeps the accent one value across four surfaces. To change a colour, change
// the token source and run `node design-system/build.mjs`.
//
// The whole palette is built per colour scheme by `buildScheme`. It used to be
// a shared `COMMON` block spread into both, which meant dark mode inherited the
// light accent tint, the light semantic fills and the light categorical set:
// only text, background and divider ever went dark. The most visible
// consequence was the one the system warns about: a selected table row in dark
// mode took the solid `#D0E1FB` tint, which covered the row's bottom hairline
// and merged it into the next row.
//
// Two shapes live side by side on purpose:
//   * `palette.semantic` / `palette.pastel`: the design-system roles. New code
//     reads these, because they say what a colour is for.
//   * MUI's `primary.lighter … darker` slots: the vendor template's names.
//     Their `dark` / `darker` mapping is asymmetric per semantic; that is
//     preserved from what shipped and is a recorded divergence, not an accident.
// ----------------------------------------------------------------------

function createGradient(color1: string, color2: string) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`
}

export type ColorSchema =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'

interface GradientsPaletteOptions {
  primary: string
  info: string
  success: string
  warning: string
  error: string
}

/** What a chart draws a result in: the bar's fill and the figure's ink. */
export interface ChartResultColors {
  fill: string
  text: string
}

/** A cost category's two colours: its chip's pastel pair and its bar's vivid. */
export interface CostCategoryColors {
  chip: ColorPair
  vivid: string
}

/**
 * Series colours for charts, in order. Each is a categorical's vivid in the
 * active scheme, the saturated hue a mark is drawn in, so two series side by
 * side are told apart at a glance. They were the categorical inks, and in
 * light every ink is near black. Booked and freestyle take `rentalType`, not a
 * position here.
 */
export interface ChartPaletteOptions {
  series: readonly string[]
  /** Booked and freestyle as bars, one hue at two lightnesses. */
  rentalType: { booking: string; freestyle: string }
  /**
   * The Costs and Business charts. Revenue and platform costs as vivids, and
   * each cost category with the pastel its chip wears and the vivid its bar
   * takes. An estimated month is the pastel with an outline in the vivid.
   */
  costs: {
    revenue: CostCategoryColors
    costs: CostCategoryColors
    categories: Record<CostCategory, CostCategoryColors>
  }
  /** A month's result: profit in the success semantic, loss in the error one. */
  profitLoss: { profit: ChartResultColors; loss: ChartResultColors }
}

export type CostCategory = keyof typeof chartTokens.costs.categories

// Categorical pastel + soft-status pairs (bg + text). The one place status /
// tag colours are defined: referenced everywhere, never re-declared.
export interface ColorPair {
  bg: string
  text: string
}
export type PastelName = keyof typeof categoricalTokens.light
export type SemanticName = keyof typeof semanticTokens.light
export type SoftStatusName = 'success' | 'error' | 'warning'

/** What kind of charge a session was, for its colour. See `palette.charge`. */
export type ChargeKind = 'supercharger' | 'ac' | 'dc'

/**
 * A semantic is a set of roles, not a single colour: `fill` for meters and
 * solid indicators, `soft` for chip backgrounds, `text` for a label that has to
 * be legible on `soft`. Reach for these by name rather than for a MUI slot.
 */
export interface SemanticRoles {
  fill: string
  brightFill: string
  soft: string
  text: string
  deep: string
}

// Status / type / tier colour maps (chip background + text). Keyed by the
// domain value (e.g. `booked`, `active`, `personal`) with a `default` fallback.
export type StatusColorMap = Record<
  string,
  { background: string; color: string }
>

/** The four colours of Google's own sign-in button, per scheme. */
export interface GoogleButtonColors {
  fill: string
  pressed: string
  edge: string
  text: string
}

/** The colours Apple's guidelines give its sign-in button, per scheme. */
export interface AppleButtonColors {
  fill: string
  pressed: string
  /** The label's, and the mark's: Apple draws its mark in the label's ink. */
  text: string
}

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string
  }
  interface SimplePaletteColorOptions {
    lighter: string
    darker: string
  }
  interface PaletteColor {
    lighter: string
    darker: string
  }
  interface Palette {
    gradients: GradientsPaletteOptions
    chart: ChartPaletteOptions
    pastel: Record<PastelName, ColorPair>
    /** The saturated hue behind each pastel. See `toVivid`. */
    vivid: Record<PastelName, string>
    semantic: Record<SemanticName, SemanticRoles>
    softStatus: Record<SoftStatusName, ColorPair>
    orange: string
    accentRing: string
    /** The accent as text, lifted in dark so it clears the surface. */
    accentText: string
    controlEdge: string
    /** The ground of a pill that holds a control and its word. See the token. */
    surfaceSunkenStrong: string
    scrim: string
    /** A scrollbar's thumb, on a transparent track. See the token. */
    scrollbarThumb: string
    /**
     * A charge's kind on the Charging page, for its pin and the word of its
     * type: Supercharger red, AC green, DC blue. See the `charge*` tokens.
     */
    charge: Record<ChargeKind, string>
    rentalStatus: StatusColorMap
    subscriptionStatus: StatusColorMap
    providerSubscriptionStatus: StatusColorMap
    subscriptionTier: StatusColorMap
    invoiceStatus: StatusColorMap
    userStatus: StatusColorMap
    vehicleApplicationStatus: StatusColorMap
    vehicleKeyStatus: StatusColorMap
    ownerKeyStatus: StatusColorMap
    vehicleAvailability: StatusColorMap
    teslaConnection: StatusColorMap
    teslaImport: StatusColorMap
    capability: StatusColorMap
    erasure: StatusColorMap
    marketingCampaignStatus: StatusColorMap
    opsSubTask: StatusColorMap
    promoCodeStatus: StatusColorMap
    provenance: StatusColorMap
    superchargerBillStatus: StatusColorMap
    businessInvoiceStatus: StatusColorMap
    tesla: PaletteColor
    /** The attention orange as a palette colour, for a control asked for by it. */
    attention: PaletteColor
    /** Google's own sign-in button, in Google's colours; see the tokens. */
    googleButton: GoogleButtonColors
    /** Apple's sign-in button, as Apple's guidelines draw it; see the tokens. */
    appleButton: AppleButtonColors
  }
  interface PaletteOptions {
    gradients: GradientsPaletteOptions
    chart: ChartPaletteOptions
    pastel: Record<PastelName, ColorPair>
    /** The saturated hue behind each pastel. See `toVivid`. */
    vivid: Record<PastelName, string>
    semantic: Record<SemanticName, SemanticRoles>
    softStatus: Record<SoftStatusName, ColorPair>
    orange: string
    accentRing: string
    /** The accent as text, lifted in dark so it clears the surface. */
    accentText: string
    controlEdge: string
    /** The ground of a pill that holds a control and its word. See the token. */
    surfaceSunkenStrong: string
    scrim: string
    /** A scrollbar's thumb, on a transparent track. See the token. */
    scrollbarThumb: string
    charge: Record<ChargeKind, string>
    rentalStatus: StatusColorMap
    subscriptionStatus: StatusColorMap
    providerSubscriptionStatus: StatusColorMap
    subscriptionTier: StatusColorMap
    invoiceStatus: StatusColorMap
    userStatus: StatusColorMap
    vehicleApplicationStatus: StatusColorMap
    vehicleKeyStatus: StatusColorMap
    ownerKeyStatus: StatusColorMap
    vehicleAvailability: StatusColorMap
    teslaConnection: StatusColorMap
    teslaImport: StatusColorMap
    capability: StatusColorMap
    erasure: StatusColorMap
    marketingCampaignStatus: StatusColorMap
    opsSubTask: StatusColorMap
    promoCodeStatus: StatusColorMap
    provenance: StatusColorMap
    superchargerBillStatus: StatusColorMap
    businessInvoiceStatus: StatusColorMap
    tesla: PaletteColorOptions
    attention: PaletteColorOptions
    googleButton: GoogleButtonColors
    appleButton: AppleButtonColors
  }
}

// The brand red is a palette colour a button can ask for by name. Without
// this a screen has to say `color="error"` and then override the background,
// which takes the fill from one decision and the ink from another: the sign in
// button drew near black on dark red the moment the error ink stopped being
// white.
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    tesla: true
  }
}

// The same name wherever a button colour flows. Not only for symmetry: several
// components take `ButtonProps` and spread them onto an `IconButton` or a
// `ButtonGroup`, so the colour unions have to stay assignable to each other.
declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    tesla: true
  }
}

declare module '@mui/material/ButtonGroup' {
  interface ButtonGroupPropsColorOverrides {
    tesla: true
  }
}

// A segmented cell asked for by the attention orange (the speeding panel's
// Dismissed). The theme draws its thumb from the semantic's soft pair.
declare module '@mui/material/ToggleButton' {
  interface ToggleButtonPropsColorOverrides {
    attention: true
  }
}

declare module '@mui/material' {
  interface Color {
    0: string
    500_8: string
    500_12: string
    500_16: string
    500_24: string
    500_32: string
    500_48: string
    500_56: string
    500_80: string
  }
}

type Scheme = 'light' | 'dark'

const toPairs = <K extends string>(
  source: Record<K, { bg: string; text: string }>,
): Record<K, ColorPair> =>
  Object.fromEntries(
    Object.entries<{ bg: string; text: string }>(source).map(([name, pair]) => [
      name,
      { bg: pair.bg, text: pair.text },
    ]),
  ) as Record<K, ColorPair>

/**
 * The saturated hue behind each pastel, for a mark on a ground this system
 * does not control.
 *
 * The chip pair is quiet on purpose, because a console surface is quiet. On a
 * photographic basemap that quiet reads as no colour at all: the fleet map
 * shipped four statuses whose marker fills were four near-whites, 1.0:1 from
 * each other, so an operator could not tell an available car from a rented one
 * without reading the pill. This is the same hue at full strength, and the ink
 * that sits on it is white rather than the pastel's own text role.
 */
const toVivid = <K extends string>(
  source: Record<K, { vivid: string }>,
): Record<K, string> =>
  Object.fromEntries(
    Object.entries<{ vivid: string }>(source).map(([name, pair]) => [
      name,
      pair.vivid,
    ]),
  ) as Record<K, string>

const buildScheme = (scheme: Scheme) => {
  const c = colorTokens[scheme]
  const sem = semanticTokens[scheme] as Record<SemanticName, SemanticRoles>

  // Accent, the one bold colour (electric blue). `info` maps onto it because
  // there is no separate info hue in the system, and `secondary` is kept
  // identical while the template's secondary slot is retired.
  const ACCENT = {
    lighter: c.accentLight,
    light: sem.info.brightFill,
    main: c.accent,
    dark: c.accentDark,
    darker: c.accentLightInk,
  }

  // The template's `dark` and `darker` slots do not agree on which end of a
  // semantic they hold: success puts its chip text in `dark`, while warning and
  // error put theirs in `darker`. Screens read those slots today, so the
  // mapping is preserved verbatim rather than normalised. New code uses
  // `theme.palette.semantic.<name>.text`.
  const SUCCESS = {
    lighter: sem.success.soft,
    light: sem.success.brightFill,
    main: sem.success.fill,
    dark: sem.success.text,
    darker: sem.success.deep,
  }
  const WARNING = {
    lighter: sem.warning.soft,
    light: sem.warning.brightFill,
    main: sem.warning.fill,
    dark: sem.warning.deep,
    darker: sem.warning.text,
  }
  const ERROR = {
    lighter: sem.error.soft,
    light: sem.error.brightFill,
    main: sem.error.fill,
    dark: sem.error.deep,
    darker: sem.error.text,
  }

  // Neutrals & ink mapped onto MUI's grey keys. The ramp is by *role*, not by
  // lightness: 0-300 are surfaces, 400-500 are borders and muted text, 600-900
  // are text. In dark mode the roles hold and the values invert, so a component
  // that asks for `grey[200]` gets a sunken panel in both schemes.
  const GREY = {
    0: c.white,
    100: c.greyLightest,
    200: c.greyLight,
    300: c.greyOutline,
    400: c.greyRegular,
    500: c.greyText, // base for the alpha greys below
    600: c.greyDark,
    700: c.greyDeep,
    800: c.greyDarker,
    900: c.ink,
    500_8: alpha(c.greyText, 0.08),
    500_12: alpha(c.greyText, 0.12),
    500_16: alpha(c.greyText, 0.16),
    500_24: alpha(c.greyText, 0.24),
    500_32: alpha(c.greyText, 0.32),
    500_48: alpha(c.greyText, 0.48),
    500_56: alpha(c.greyText, 0.56),
    500_80: alpha(c.greyText, 0.8),
  }

  // Categorical pastel set: same category always maps to the same colour.
  const PASTEL = toPairs(categoricalTokens[scheme])
  const VIVID = toVivid(categoricalTokens[scheme])
  const SOFT: Record<SoftStatusName, ColorPair> = {
    success: { bg: sem.success.soft, text: sem.success.text },
    error: { bg: sem.error.soft, text: sem.error.text },
    warning: { bg: sem.warning.soft, text: sem.warning.text },
  }

  const pastel = (name: PastelName) => ({
    background: PASTEL[name].bg,
    color: PASTEL[name].text,
  })
  const soft = (name: SoftStatusName) => ({
    background: SOFT[name].bg,
    color: SOFT[name].text,
  })

  // Status/tag colour maps, sourced from the pastel set (defined once above)
  // so the whole app stays consistent and nothing hardcodes per-component hex.
  const SUBSCRIPTION_TIER_TAG_COLORS = {
    personal: { background: ACCENT.main, color: c.onAccent },
    business: pastel('cyan'),
    default: pastel('yellow'),
  }

  const SUBSCRIPTION_STATUS_TAG_COLORS = {
    pending: pastel('grey'),
    rejected: pastel('coral'),
    active: soft('success'),
    cancelled: soft('error'),
    default: pastel('yellow'),
  }

  // The provider's own billing subscription, which is a Stripe subscription
  // and carries Stripe's vocabulary. It used to share the map above, so a
  // plan and a rental read from one set of colours while meaning different
  // things by the same word, and the four states nobody had listed fell to a
  // yellow default while the sidebar badge drew the same account in red.
  const PROVIDER_SUBSCRIPTION_STATUS_TAG_COLORS = {
    active: soft('success'),
    trialing: pastel('blue'),
    past_due: soft('warning'),
    unpaid: soft('error'),
    canceled: soft('error'),
    incomplete: soft('warning'),
    incomplete_expired: soft('error'),
    paused: pastel('blue'),
    // Not grey and not green: a state nobody has mapped is not a healthy one.
    default: soft('warning'),
  }

  const INVOICE_STATUS_TAG_COLORS = {
    // `open` is what the API sends for an issued, unpaid invoice. It used to
    // fall through to the yellow default, which read correctly by accident; it
    // is named here so the meaning is deliberate.
    open: pastel('yellow'),
    pending: pastel('grey'),
    failed: soft('error'),
    paid: soft('success'),
    refunded: pastel('lavender'),
    default: pastel('yellow'),
  }

  const RENTAL_STATUS_TAG_COLORS = {
    booked: pastel('lavender'),
    // `booking` is the rental *type* counterpart of the `booked` status: same
    // colour so a StatusChip can render either value consistently.
    booking: pastel('lavender'),
    freestyle: pastel('peach'),
    started: pastel('blue'),
    completed: soft('success'),
    cancelled: soft('error'),
    // A rental that failed is a state someone has to resolve, not a neutral
    // one. It used to fall through to the grey default.
    failed: soft('error'),
    pending: pastel('yellow'),
    default: pastel('grey'),
  }

  // The jobs a vehicle task is made of. Categorical and not semantic, for the
  // same reason the feedback tags are: cleaning an interior does not rank
  // above refilling a fluid, and neither is a system state. The point of the
  // colour is that a task holding three of five can be told from a task
  // holding a different three without reading five labels, so what matters
  // is that the five differ from each other, not what any one of them means.
  const OPS_SUB_TASK_TAG_COLORS = {
    cleaningExterior: pastel('peach'),
    cleaningInterior: pastel('lavender'),
    refillingFluid: pastel('cyan'),
    charging: pastel('mint'),
    resetSettings: pastel('yellow'),
    default: pastel('grey'),
  }

  // Where a promo code stands. Unlike the work types above this IS a status
  // (four of the five say the code cannot be redeemed right now), so it takes
  // semantics where there is one to take and a pastel only for `scheduled`,
  // which is a code doing exactly what it was set up to do.
  const PROMO_CODE_STATUS_TAG_COLORS = {
    active: soft('success'),
    scheduled: pastel('blue'),
    expired: pastel('grey'),
    exhausted: pastel('yellow'),
    // Somebody switched this off. It is the one state on the list that a
    // person chose and the one that can be undone from the console, which is
    // why it is the only one drawn in the tone that means stopped.
    disabled: soft('error'),
    default: pastel('grey'),
  }

  // Vehicle application review states. These used to render with MUI's default
  // chip colours, which is why they were the only chips in the console that did
  // not match the pastel set.
  const VEHICLE_APPLICATION_STATUS_TAG_COLORS = {
    submitted: pastel('yellow'),
    approved: soft('success'),
    rejected: soft('error'),
    deactivated: pastel('grey'),
    default: pastel('grey'),
  }

  // Signed-command key enrolment. `notSupported` is a healthy state for a
  // vehicle that does not need a key, so it is not an error and not a success
  // either, and `unknown` means the Fleet API is not set up at all.
  const VEHICLE_KEY_STATUS_TAG_COLORS = {
    ok: soft('success'),
    notSupported: pastel('grey'),
    missing: soft('error'),
    unknown: pastel('grey'),
    default: pastel('grey'),
  }

  // What an operator can do with a vehicle right now. Free is the good state
  // and takes the success semantic; rented shares the blue the rental
  // vocabulary gives a rental in progress, so the same car reads the same on
  // the fleet list and on the rental it belongs to. Driving is a car on the
  // move that is not rented: its own pastel, beside the rental blue and not
  // the same, because who is driving it is exactly the open question.
  const VEHICLE_AVAILABILITY_TAG_COLORS = {
    free: soft('success'),
    rented: pastel('blue'),
    driving: pastel('lavender'),
    disabled: pastel('grey'),
    default: pastel('grey'),
  }

  // A withdrawn grant is an error, not a warning: nothing on the account
  // works until somebody re-authorises, and the vehicles it sourced cannot
  // be commanded. Warning would put it in the same tone as a service due.
  // Beast's owner key, the key added at the car with a key card. Amber, the
  // tone of the notice it is drawn in: nothing is broken, there is a step to
  // take at the car. Never the fleet key's map, whose `missing` is an error
  // red about a different key.
  const OWNER_KEY_STATUS_TAG_COLORS = {
    needed: soft('warning'),
    default: pastel('grey'),
  }

  const TESLA_CONNECTION_TAG_COLORS = {
    connected: soft('success'),
    revoked: soft('error'),
    default: pastel('grey'),
  }

  // Nothing here is a failure, so nothing here is red. A car already in the
  // fleet is the operator's own success; one another provider holds is simply
  // not theirs to take, which is the neutral the capability map uses for the
  // same reason. `started` is the odd one out and sits on an offered car
  // rather than a withheld one: it is the only state with something left to
  // do, so it is the only one that carries warmth.
  const TESLA_IMPORT_TAG_COLORS = {
    imported: soft('success'),
    importedByAnotherProvider: pastel('grey'),
    started: soft('warning'),
    default: pastel('grey'),
  }

  // Grey, and only grey: all three of them. A capability that is off is not a
  // warning and not an error: nothing has gone wrong and there is nothing to
  // fix. It is the absence of a thing, which is what the neutral pastel is for.
  //
  // The three states differ by glyph and by word, not by hue, and that is the
  // point: a lock and a minus circle say who can change it, where three
  // colours would have said how bad it is, which is not the question.
  const CAPABILITY_TAG_COLORS = {
    unavailable: pastel('grey'),
    locked: pastel('grey'),
    withdrawn: pastel('grey'),
    default: pastel('grey'),
  }

  // One hue for all six, and it is the neutral one. A deletion request that is
  // waiting is not a fault and neither is one held back: a ban we placed
  // deliberately, a balance still owed and a rental still running are all
  // states of an orderly process. Colouring the held rows as errors would put
  // an operator's own ban in the same tone as an outage.
  //
  // What separates them is the glyph and the word, which is also what survives
  // the roughly one operator in twelve who cannot separate red from green.
  const ERASURE_TAG_COLORS = {
    scheduled: pastel('grey'),
    banned: pastel('grey'),
    outstandingDebt: pastel('grey'),
    openRiskRecord: pastel('grey'),
    beastStaff: pastel('grey'),
    partnerAccountActive: pastel('grey'),
    activeRental: pastel('grey'),
    partnerFleetInUse: pastel('grey'),
    default: pastel('grey'),
  }

  // A campaign's stages, not its health. Pending is a campaign that has been
  // written and has not reached its send time, which is a resting state and
  // takes the neutral rather than a warning: nothing is wrong with a campaign
  // scheduled for Friday. Processing warms up because it is the one stage with
  // work part-done, sending takes the in-progress blue the rental vocabulary
  // gives the same idea, and completed is the success.
  //
  // Cancelled is the only red, and it is red because somebody stopped it.
  // Nothing here fails on its own: a campaign that cannot reach a device
  // counts that in the fails column and still completes.
  const MARKETING_CAMPAIGN_STATUS_TAG_COLORS = {
    pending: pastel('grey'),
    processing: soft('warning'),
    sending: pastel('blue'),
    completed: soft('success'),
    cancelled: soft('error'),
    default: pastel('grey'),
  }

  // Where a cost figure came from. Booked is the one fact, so it takes the
  // success pair; a figure read off the vendor's own billing is a report, the
  // informational blue; one Beast worked out from its meters and a price list
  // is navy (`estimated`); a free plan or a month before Beast used the
  // vendor, where nothing is owed, is quiet grey; and a fixed fee carried
  // forward until its invoice arrives is the warning pair, because it is a
  // figure somebody still has to replace.
  const PROVENANCE_TAG_COLORS = {
    booked: soft('success'),
    fromVendor: { background: sem.info.soft, color: sem.info.text },
    // Navy, asked for on 2026-10-09: a worked-out figure reads apart from
    // the grey Free plan and the pale blue of a vendor's own figure.
    estimated: { background: c.estimated, color: c.onEstimated },
    expected: soft('warning'),
    freePlan: pastel('grey'),
    notInUse: pastel('grey'),
    default: pastel('grey'),
  }

  // Where a Supercharger session stands with the renter. A status, so it
  // takes semantics where there is one: charged is the success, unpaid the
  // error (the tries are over and it waits in the rental's price), and
  // failing the attention orange: a charge went wrong and the automatic tries
  // are still going, a step short of the red. Peach is that orange, the
  // attention semantic's own soft and text pair in both schemes, and `SOFT`
  // holds no attention entry to name it by. Waiting is in progress and takes
  // the blue the rental vocabulary gives a rental under way. Due will be
  // charged on the next run and nothing is wrong with it, so it is cyan, the
  // hue between waiting's blue and charged's green. Waived is yellow, asked
  // for on 2026-10-07: the fleet gave it up, which is not a fault. Not billed
  // is the absence of a charge, so it is the neutral, and the reason beside
  // it says which rule kept it off the renter.
  //
  // No two states share a colour. Due was yellow and failing the warning
  // soft, 1.3 apart (CIEDE2000) in light and read as one chip. Cyan's nearest
  // neighbour here is not billed's grey, 7.8 in light and 9.5 in dark; the
  // freed lavender would have sat 6.4 from waiting's blue in dark.
  const SUPERCHARGER_BILL_STATUS_TAG_COLORS = {
    waiting: pastel('blue'),
    due: pastel('cyan'),
    charged: soft('success'),
    failing: pastel('peach'),
    unpaid: soft('error'),
    waived: pastel('yellow'),
    not_billed: pastel('grey'),
    default: pastel('grey'),
  }

  // Where a document Beast issues in a fleet's name stands. Issued is done
  // and frozen: the success. Held is nothing issued and a person invoicing it
  // by hand, so it is the attention orange, peach as Failing is: somebody has
  // something to do, and nothing failed. The row says why in words.
  const BUSINESS_INVOICE_STATUS_TAG_COLORS = {
    issued: soft('success'),
    held: pastel('peach'),
    default: pastel('grey'),
  }

  const costColors = (name: PastelName): CostCategoryColors => ({
    chip: PASTEL[name],
    vivid: VIVID[name],
  })
  const result = ({
    semantic,
    slot,
  }: {
    semantic: SemanticName
    slot: keyof SemanticRoles
  }): ChartResultColors => ({
    fill: sem[semantic][slot],
    text: sem[semantic].text,
  })

  const USER_STATUS_TAG_COLORS = {
    valid: soft('success'),
    blocked: soft('error'),
    deleted: soft('warning'),
    default: pastel('grey'),
  }

  return {
    mode: scheme,
    common: { black: c.pureBlack, white: c.pureWhite },
    primary: { ...ACCENT, contrastText: c.onAccent },
    secondary: { ...ACCENT, contrastText: c.onAccent },
    info: { ...ACCENT, contrastText: c.onAccent },
    // The ink each fill can actually carry, not white by default. White is
    // 3.6:1 on the green and 3.1:1 on the red, so a solid Free chip and a
    // danger button were both stating a background without stating a legible
    // ink. `info` and `primary` keep white, which clears 4.8:1 on the accent.
    success: { ...SUCCESS, contrastText: c.onSuccess },
    // A yellow fill needs dark text in both schemes.
    warning: { ...WARNING, contrastText: c.onWarning },
    error: { ...ERROR, contrastText: c.onError },
    // Its own ink, not the error semantic's. `tesla` borrows the error slots
    // for shape and then replaces `main` with a much darker red, so the two
    // fills need opposite inks: white is 5.9:1 on the brand red and 3.1:1 on
    // the error fill, and near black is the other way round.
    // Its own fill, its own hover and its own ink. It borrows the error slots
    // for shape only: `main` is the brand red, `dark` is what MUI hovers a
    // contained button to, and `contrastText` is white because the brand red is
    // a dark fill where the error fill is a light one. Without the last two,
    // hovering the sign in button went lighter and shifted hue off the brand,
    // and its label went near black when the error ink did.
    tesla: {
      ...ERROR,
      main: c.tesla,
      dark: c.teslaPressed,
      contrastText: c.onAccent,
    },
    // The attention orange, so a control can be asked for by it: MUI reads
    // `palette[color].main` for a toggle's selected look before the theme gets
    // a say, and a colour with no entry here throws. The speeding panel's
    // Dismissed is the first cell to ask (2026-11-01). Slots as the warning
    // keeps them, and the same dark ink on the bright warm fill.
    attention: {
      lighter: sem.attention.soft,
      light: sem.attention.brightFill,
      main: sem.attention.fill,
      dark: sem.attention.deep,
      darker: sem.attention.text,
      contrastText: c.onWarning,
    },
    // Not a palette colour a button asks for by name: Google's button is an
    // outlined control in Google's fill, edge and ink, all four of them its
    // owner's. `GoogleSignInButton` is the one place that draws it.
    googleButton: {
      fill: c.google,
      pressed: c.googlePressed,
      edge: c.googleEdge,
      text: c.googleText,
    },
    // Apple's button the same way: black on a light page and white on a dark
    // one, its label and mark in the other. `AppleSignInButton` draws it.
    appleButton: {
      fill: c.apple,
      pressed: c.applePressed,
      text: c.appleText,
    },
    grey: GREY,
    orange: sem.attention.fill,
    accentRing: c.accentRing,
    // The accent drawn as text rather than as a fill. `primary.main` is the
    // fill, the same blue in both schemes, and on the dark surface it is 3.5:1:
    // a button can carry that, a word in a sentence cannot. This is the value
    // the token source lifts in dark for exactly that case.
    accentText: c.accentText,
    // The boundary of a control whose resting state is "nothing selected".
    // Not `divider` and not `grey[400]`: a hairline separates two things that
    // are already visible, where this is the only thing saying the control is
    // there at all. See the note on `color.role.controlEdge` in the token
    // source for the ratios, and `controls.test.ts` for the floor.
    controlEdge: c.controlEdge,
    // One step under `surfaceSunken`, so a pill that holds a control and a
    // word is told from the card by its ground rather than by a ring drawn
    // round the word. `surfaceSunken` cannot do it: it is 1.05:1 on a card
    // and it is also the table row hover fill, so a pill on it disappears
    // under the pointer.
    surfaceSunkenStrong: c.surfaceSunkenStrong,
    scrim: c.scrim,
    // Where a platform draws a scrollbar that takes room. The token's own
    // role, at the control edge's values, and CssBaseline puts it on the
    // root so every scroller in the console inherits it.
    scrollbarThumb: c.scrollbarThumb,
    // The three kinds of charge, by where the car charged and how: asked for
    // on 2026-10-09 so a Supercharger, an AC and a DC session read apart at
    // a glance on the Charging page.
    charge: {
      supercharger: c.chargeSupercharger,
      ac: c.chargeAc,
      dc: c.chargeDc,
    },
    gradients: {
      primary: createGradient(ACCENT.light, ACCENT.main),
      info: createGradient(ACCENT.light, ACCENT.main),
      success: createGradient(SUCCESS.light, SUCCESS.main),
      warning: createGradient(WARNING.light, WARNING.main),
      error: createGradient(ERROR.light, ERROR.main),
    },
    chart: {
      series: chartTokens.series.map((name) => VIVID[name]),
      rentalType: chartTokens.rentalType[scheme],
      costs: {
        revenue: costColors(chartTokens.costs.revenue),
        costs: costColors(chartTokens.costs.costs),
        categories: Object.fromEntries(
          Object.entries(chartTokens.costs.categories).map(([key, name]) => [
            key,
            costColors(name),
          ]),
        ) as Record<CostCategory, CostCategoryColors>,
      },
      profitLoss: {
        profit: result(chartTokens.profitLoss.profit),
        loss: result(chartTokens.profitLoss.loss),
      },
    },
    subscriptionTier: SUBSCRIPTION_TIER_TAG_COLORS,
    subscriptionStatus: SUBSCRIPTION_STATUS_TAG_COLORS,
    providerSubscriptionStatus: PROVIDER_SUBSCRIPTION_STATUS_TAG_COLORS,
    invoiceStatus: INVOICE_STATUS_TAG_COLORS,
    rentalStatus: RENTAL_STATUS_TAG_COLORS,
    userStatus: USER_STATUS_TAG_COLORS,
    vehicleApplicationStatus: VEHICLE_APPLICATION_STATUS_TAG_COLORS,
    vehicleKeyStatus: VEHICLE_KEY_STATUS_TAG_COLORS,
    ownerKeyStatus: OWNER_KEY_STATUS_TAG_COLORS,
    vehicleAvailability: VEHICLE_AVAILABILITY_TAG_COLORS,
    teslaConnection: TESLA_CONNECTION_TAG_COLORS,
    teslaImport: TESLA_IMPORT_TAG_COLORS,
    capability: CAPABILITY_TAG_COLORS,
    erasure: ERASURE_TAG_COLORS,
    marketingCampaignStatus: MARKETING_CAMPAIGN_STATUS_TAG_COLORS,
    opsSubTask: OPS_SUB_TASK_TAG_COLORS,
    promoCodeStatus: PROMO_CODE_STATUS_TAG_COLORS,
    provenance: PROVENANCE_TAG_COLORS,
    superchargerBillStatus: SUPERCHARGER_BILL_STATUS_TAG_COLORS,
    businessInvoiceStatus: BUSINESS_INVOICE_STATUS_TAG_COLORS,
    pastel: PASTEL,
    vivid: VIVID,
    semantic: sem,
    softStatus: SOFT,
    text: {
      primary: c.text1,
      secondary: c.text2,
      disabled: c.textDisabled,
    },
    background: {
      paper: c.surface,
      default: c.surface2,
      neutral: c.surfaceSunken,
    },
    divider: c.border,
    action: {
      active: c.greyDark,
      hover: GREY[500_8],
      selected: GREY[500_16],
      disabled: GREY[500_80],
      disabledBackground: GREY[500_24],
      focus: GREY[500_24],
      hoverOpacity: 0.08,
      disabledOpacity: 0.48,
    },
  }
}

const palette = {
  light: buildScheme('light'),
  dark: buildScheme('dark'),
} as const

export default palette
